import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useConfirm } from "material-ui-confirm";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { AppAutocomplete, AppButton, AppCheckbox, AppIconButton, AppTextField } from "../../../components/form";
import { AppFormField, AppFormGrid, AppSlideOver, PageSectionCard } from "../../../components/layout";
import PageEmptyState from "../../../components/Page/PageEmptyState";
import { GRID_FULL, GRID_HALF } from "../../../lib/ui/formLayout";
import { isConfirmCancelled, useDeleteConfirm } from "../../../hooks/useDeleteConfirm";
import { BASE_PATH } from "../../../api/api";
import { embedConfigService, type EmbedConfig } from "../../../services/embedConfigService";
import { workspaceService, type WorkspaceSummary } from "../../../services/workspaceService";
import {
  integrationClientService,
  MEMBER_ROLES,
  type IntegrationClient,
  type IntegrationMemberRole,
  type IntegrationClientPayload,
  type IntegrationSecretCreated,
  type IntegrationStats,
  type IntegrationUser,
  type IntegrationUserPage,
} from "../../../services/integrationClientService";
import { buildEmbedSnippet } from "../../../utils/embedSnippet";
import {
  INTEGRATION_SECRET_HEADER,
  buildIntegrationSnippet,
  integrationApiBase,
  type IntegrationSnippetLanguage,
} from "../../../utils/integrationSnippets";

const DEFAULT_GRACE_DAYS = 7;
const USERS_PAGE_SIZE = 25;
const SNIPPET_LANGUAGES: IntegrationSnippetLanguage[] = ["curl", "node", "dotnet", "page"];

type FormState = {
  id?: string;
  name: string;
  enabled: boolean;
  /** The customer workspace whose data this integration's end users query. */
  workspaceId: string;
  memberRole: IntegrationMemberRole;
  chatbotOnly: boolean;
  embedConfigId: string;
  ipsText: string;
  rateLimit: string;
  sendWelcomeMail: boolean;
  autoSetupBeas: boolean;
};

type DetailTab = "secrets" | "users" | "usage" | "guide";

const emptyForm = (): FormState => ({
  name: "",
  enabled: true,
  workspaceId: "",
  memberRole: "USER",
  chatbotOnly: true,
  embedConfigId: "",
  ipsText: "",
  rateLimit: "",
  sendWelcomeMail: false,
  autoSetupBeas: true,
});

function toForm(row: IntegrationClient): FormState {
  return {
    id: row.id,
    name: row.name,
    enabled: row.enabled,
    workspaceId: row.workspaceId || "",
    memberRole: row.memberRole || "USER",
    chatbotOnly: row.chatbotOnly,
    embedConfigId: row.embedConfigId,
    ipsText: (row.allowedIps || []).join("\n"),
    rateLimit: row.rateLimitPerMinute ? String(row.rateLimitPerMinute) : "",
    sendWelcomeMail: row.sendWelcomeMail,
    autoSetupBeas: row.autoSetupBeas,
  };
}

function toPayload(form: FormState): IntegrationClientPayload {
  const limit = Number(form.rateLimit);
  return {
    name: form.name.trim(),
    enabled: form.enabled,
    workspaceId: form.workspaceId,
    memberRole: form.memberRole,
    chatbotOnly: form.chatbotOnly,
    embedConfigId: form.embedConfigId,
    allowedIps: form.ipsText
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean),
    rateLimitPerMinute: Number.isFinite(limit) && limit > 0 ? Math.floor(limit) : null,
    sendWelcomeMail: form.sendWelcomeMail,
    autoSetupBeas: form.autoSetupBeas,
  };
}

const formatDate = (value?: string | null) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
};

const monoSx = { fontFamily: "monospace", wordBreak: "break-all" } as const;
const preSx = {
  ...monoSx,
  m: 0,
  p: 1.5,
  borderRadius: 1,
  bgcolor: "action.hover",
  fontSize: 12,
  whiteSpace: "pre-wrap",
} as const;

const SETUP_COLORS: Record<string, "default" | "success" | "warning" | "error" | "info"> = {
  NOT_REQUESTED: "default",
  PENDING: "info",
  COMPLETE: "success",
  FAILED: "error",
};

/**
 * External systems that create Connect AI users (own workspace, Free package) through the integration API
 * and sign them in to an embedded chat with one-time login tickets.
 */
export default function IntegrationsPanel() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { confirmDelete } = useDeleteConfirm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [rows, setRows] = useState<IntegrationClient[]>([]);
  const [embeds, setEmbeds] = useState<EmbedConfig[]>([]);
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([]);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [formOpen, setFormOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tab, setTab] = useState<DetailTab>("secrets");
  const [createdSecret, setCreatedSecret] = useState<IntegrationSecretCreated | null>(null);
  const [users, setUsers] = useState<IntegrationUserPage | null>(null);
  const [usersPage, setUsersPage] = useState(0);
  const [stats, setStats] = useState<IntegrationStats | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [language, setLanguage] = useState<IntegrationSnippetLanguage>("curl");

  const selected = useMemo(() => rows.find((r) => r.id === selectedId) || null, [rows, selectedId]);
  // End users are members of the integration's customer workspace, so a platform-wide embed or one bound
  // to that same workspace can admit them.
  const platformEmbeds = useMemo(() => embeds.filter((e) => !e.workspaceId), [embeds]);
  const selectedEmbed = useMemo(
    () => embeds.find((e) => e.id === selected?.embedConfigId) || null,
    [embeds, selected?.embedConfigId]
  );
  const apiBase = useMemo(() => integrationApiBase(BASE_PATH, window.location.origin), []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, embedList, workspaceList] = await Promise.all([
        integrationClientService.list(),
        embedConfigService.list().catch(() => null),
        workspaceService.listAll().catch(() => null),
      ]);
      setRows(Array.isArray(list) ? list : []);
      setEmbeds(Array.isArray(embedList) ? embedList : []);
      setWorkspaces(Array.isArray(workspaceList) ? workspaceList : []);
      if (embedList === null) setError(t("superAdmin.integrations.embedsLoadError"));
      else if (workspaceList === null) setError(t("superAdmin.integrations.workspacesLoadError"));
    } catch {
      setError(t("superAdmin.integrations.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadDetail = useCallback(
    async (id: string, which: DetailTab, page: number) => {
      if (which !== "users" && which !== "usage") return;
      setDetailLoading(true);
      try {
        if (which === "users") setUsers(await integrationClientService.users(id, page, USERS_PAGE_SIZE));
        else setStats(await integrationClientService.stats(id));
      } catch {
        setError(t("superAdmin.integrations.detailError"));
      } finally {
        setDetailLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    if (selectedId) void loadDetail(selectedId, tab, usersPage);
  }, [selectedId, tab, usersPage, loadDetail]);

  const flash = (message: string) => {
    setError(null);
    setSuccess(message);
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      flash(t("superAdmin.integrations.copied"));
    } catch {
      setError(t("superAdmin.integrations.copyError"));
    }
  };

  const select = (id: string) => {
    setSelectedId(id);
    setUsersPage(0);
    setUsers(null);
    setStats(null);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
    setForm(emptyForm());
  };

  const startCreate = () => {
    setForm({ ...emptyForm(), embedConfigId: platformEmbeds[0]?.id || "" });
    setFormOpen(true);
    setError(null);
  };

  const startEdit = (row: IntegrationClient) => {
    setForm(toForm(row));
    setFormOpen(true);
    setError(null);
  };

  const onSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const saved = form.id
        ? await integrationClientService.update(form.id, toPayload(form))
        : await integrationClientService.create(toPayload(form));
      setFormOpen(false);
      setForm(emptyForm());
      await load();
      select(saved.id);
      if (!form.id) {
        // A new client is unusable without a secret: issue the first one right away.
        setCreatedSecret(await integrationClientService.createSecret(saved.id));
        await load();
      } else {
        flash(t("superAdmin.integrations.saveSuccess"));
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || t("superAdmin.integrations.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (row: IntegrationClient) => {
    try {
      await confirmDelete({ itemName: row.name });
    } catch (e) {
      if (isConfirmCancelled(e)) return;
      throw e;
    }
    try {
      await integrationClientService.remove(row.id);
      if (selectedId === row.id) setSelectedId(null);
      flash(t("superAdmin.integrations.deleteSuccess"));
      await load();
    } catch {
      setError(t("superAdmin.integrations.deleteError"));
    }
  };

  const onCreateSecret = async (row: IntegrationClient) => {
    try {
      setCreatedSecret(await integrationClientService.createSecret(row.id));
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || t("superAdmin.integrations.secretError"));
    }
  };

  const onRotateSecret = async (row: IntegrationClient, secretId: string) => {
    try {
      await confirm({
        title: t("superAdmin.integrations.rotateTitle"),
        description: t("superAdmin.integrations.rotateDescription", { days: DEFAULT_GRACE_DAYS }),
        confirmationText: t("superAdmin.integrations.rotate"),
        cancellationText: t("common.cancel"),
      });
    } catch (e) {
      if (isConfirmCancelled(e)) return;
      throw e;
    }
    try {
      setCreatedSecret(await integrationClientService.rotateSecret(row.id, secretId, DEFAULT_GRACE_DAYS));
      await load();
    } catch {
      setError(t("superAdmin.integrations.secretError"));
    }
  };

  const onRevokeSecret = async (row: IntegrationClient, secretId: string) => {
    try {
      await confirm({
        title: t("superAdmin.integrations.revokeTitle"),
        description: t("superAdmin.integrations.revokeDescription"),
        confirmationText: t("superAdmin.integrations.revoke"),
        cancellationText: t("common.cancel"),
        confirmationButtonProps: { color: "error", variant: "contained" },
      });
    } catch (e) {
      if (isConfirmCancelled(e)) return;
      throw e;
    }
    try {
      await integrationClientService.revokeSecret(row.id, secretId);
      flash(t("superAdmin.integrations.revokeSuccess"));
      await load();
    } catch {
      setError(t("superAdmin.integrations.secretError"));
    }
  };

  const onRetrySetup = async (user: IntegrationUser) => {
    if (!selected) return;
    try {
      await integrationClientService.retrySetup(selected.id, user.id);
      flash(t("superAdmin.integrations.retrySetupStarted"));
      await loadDetail(selected.id, "users", usersPage);
    } catch {
      setError(t("superAdmin.integrations.detailError"));
    }
  };

  const formInvalid = !form.name.trim() || !form.embedConfigId || !form.workspaceId;

  if (loading) {
    return (
      <Stack alignItems="center" py={4} sx={{ width: "100%" }}>
        <CircularProgress size={28} />
      </Stack>
    );
  }

  type EmbedOption = { id: string; name: string; disabledReason?: string; note?: string };
  type WorkspaceOption = { id: string; name: string };
  const workspaceOptions: WorkspaceOption[] = [...workspaces]
    .map((w) => ({ id: w.id, name: w.name }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const workspaceOption = (id: string) => workspaceOptions.find((o) => o.id === id) ?? null;
  /** True when the embed may admit end users of {@code workspaceId}. */
  const embedBelongsTo = (embedId: string, workspaceId: string) => {
    if (!embedId) return false;
    const embed = embeds.find((e) => e.id === embedId);
    return !!embed && (!embed.workspaceId || embed.workspaceId === workspaceId);
  };
  // All embeds are listed so an empty choice is explained: workspace-bound ones cannot be selected.
  const toEmbedOption = (e: EmbedConfig): EmbedOption => ({
    id: e.id,
    name: e.name,
    // Only an embed of another workspace is out: a platform-wide one, or one of the chosen customer
    // workspace, can admit this integration's end users.
    disabledReason:
      e.workspaceId && e.workspaceId !== form.workspaceId
        ? t("superAdmin.integrations.embedWorkspaceBound", { workspace: e.workspaceName || e.workspaceId })
        : undefined,
    note: e.authProviders?.ticket !== true ? t("superAdmin.integrations.embedTicketOff") : undefined,
  });
  const embedOptions = [...embeds]
    .sort(
      (a, b) =>
        Number(!!a.workspaceId && a.workspaceId !== form.workspaceId) -
        Number(!!b.workspaceId && b.workspaceId !== form.workspaceId)
    )
    .map(toEmbedOption);
  const embedOption = (id: string) => embedOptions.find((o) => o.id === id) ?? null;

  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      {error && !formOpen && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <PageSectionCard
        title={t("superAdmin.integrations.listTitle")}
        description={t("superAdmin.integrations.description")}
        actions={
          <AppButton size="small" variant="contained" startIcon={<AddOutlinedIcon />} onClick={startCreate}>
            {t("superAdmin.integrations.create")}
          </AppButton>
        }
        noPadding={rows.length > 0}
      >
        {platformEmbeds.length === 0 && (
          <Alert severity="info" sx={{ m: rows.length > 0 ? 1.5 : 0, mb: 1.5 }}>
            {t("superAdmin.integrations.noPlatformEmbed")}
          </Alert>
        )}
        {rows.length === 0 ? (
          <PageEmptyState
            message={t("superAdmin.integrations.empty")}
            actionLabel={t("superAdmin.integrations.create")}
            onAction={startCreate}
            actionIcon={<AddOutlinedIcon />}
          />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("superAdmin.integrations.name")}</TableCell>
                <TableCell>{t("superAdmin.integrations.embed")}</TableCell>
                <TableCell>{t("superAdmin.integrations.status")}</TableCell>
                <TableCell align="right">{t("superAdmin.integrations.users")}</TableCell>
                <TableCell align="right">{t("superAdmin.integrations.activeSecrets")}</TableCell>
                <TableCell align="right">{t("common.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  selected={selectedId === row.id}
                  sx={{ cursor: "pointer" }}
                  onClick={() => select(row.id)}
                >
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.embedName || "—"}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={row.enabled ? "success" : "default"}
                      label={row.enabled ? t("superAdmin.integrations.enabledShort") : t("superAdmin.integrations.disabledShort")}
                    />
                  </TableCell>
                  <TableCell align="right">{row.userCount}</TableCell>
                  <TableCell align="right">{row.secrets.filter((s) => s.active).length}</TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                      <AppIconButton size="small" aria-label={t("common.edit")} title={t("common.edit")} onClick={() => startEdit(row)}>
                        <EditOutlinedIcon fontSize="small" />
                      </AppIconButton>
                      <AppIconButton
                        size="small"
                        color="error"
                        aria-label={t("common.delete")}
                        title={t("common.delete")}
                        onClick={() => void onDelete(row)}
                      >
                        <DeleteOutlineOutlinedIcon fontSize="small" />
                      </AppIconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </PageSectionCard>

      {selected && (
        <PageSectionCard title={t("superAdmin.integrations.detailTitle", { name: selected.name })} noPadding>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 1.5, borderBottom: 1, borderColor: "divider" }}>
            <Tab value="secrets" label={t("superAdmin.integrations.tabSecrets")} />
            <Tab value="users" label={t("superAdmin.integrations.tabUsers")} />
            <Tab value="usage" label={t("superAdmin.integrations.tabUsage")} />
            <Tab value="guide" label={t("superAdmin.integrations.tabGuide")} />
          </Tabs>
          <Box sx={{ p: 2 }}>
            {tab === "secrets" && (
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  {t("superAdmin.integrations.secretsHint", { header: INTEGRATION_SECRET_HEADER })}
                </Typography>
                <Box>
                  <AppButton size="small" startIcon={<AddOutlinedIcon />} onClick={() => void onCreateSecret(selected)}>
                    {t("superAdmin.integrations.newSecret")}
                  </AppButton>
                </Box>
                {selected.secrets.length === 0 ? (
                  <Alert severity="warning">{t("superAdmin.integrations.noSecrets")}</Alert>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t("superAdmin.integrations.secretPrefix")}</TableCell>
                        <TableCell>{t("superAdmin.integrations.status")}</TableCell>
                        <TableCell>{t("superAdmin.integrations.createdAt")}</TableCell>
                        <TableCell>{t("superAdmin.integrations.lastUsedAt")}</TableCell>
                        <TableCell align="right">{t("common.actions")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selected.secrets.map((s) => (
                        <TableRow key={s.id}>
                          <TableCell>
                            <Typography variant="caption" sx={monoSx}>
                              {s.prefix}…
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              variant="outlined"
                              color={s.active ? (s.graceUntil ? "warning" : "success") : "default"}
                              label={
                                !s.active
                                  ? t("superAdmin.integrations.secretInactive")
                                  : s.graceUntil
                                    ? t("superAdmin.integrations.secretGrace", { date: formatDate(s.graceUntil) })
                                    : t("superAdmin.integrations.secretActive")
                              }
                            />
                          </TableCell>
                          <TableCell>{formatDate(s.createdAt)}</TableCell>
                          <TableCell>{formatDate(s.lastUsedAt)}</TableCell>
                          <TableCell align="right">
                            {s.active && (
                              <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                                {!s.graceUntil && (
                                  <AppIconButton
                                    size="small"
                                    aria-label={t("superAdmin.integrations.rotate")}
                                    title={t("superAdmin.integrations.rotate")}
                                    onClick={() => void onRotateSecret(selected, s.id)}
                                  >
                                    <AutorenewOutlinedIcon fontSize="small" />
                                  </AppIconButton>
                                )}
                                <AppIconButton
                                  size="small"
                                  color="error"
                                  aria-label={t("superAdmin.integrations.revoke")}
                                  title={t("superAdmin.integrations.revoke")}
                                  onClick={() => void onRevokeSecret(selected, s.id)}
                                >
                                  <BlockOutlinedIcon fontSize="small" />
                                </AppIconButton>
                              </Stack>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Stack>
            )}

            {tab === "users" && (
              detailLoading && !users ? (
                <Stack alignItems="center" py={3}>
                  <CircularProgress size={24} />
                </Stack>
              ) : !users || users.content.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t("superAdmin.integrations.noUsers")}
                </Typography>
              ) : (
                <>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t("superAdmin.integrations.userEmail")}</TableCell>
                        <TableCell>{t("superAdmin.integrations.userName")}</TableCell>
                        <TableCell>{t("superAdmin.integrations.externalUserId")}</TableCell>
                        <TableCell>{t("superAdmin.integrations.setup")}</TableCell>
                        <TableCell>{t("superAdmin.integrations.lastLoginAt")}</TableCell>
                        <TableCell align="right">{t("common.actions")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.content.map((u) => (
                        <TableRow key={u.id}>
                          <TableCell>
                            {u.email}
                            {!u.enabled && (
                              <Chip size="small" sx={{ ml: 1 }} label={t("superAdmin.integrations.disabledShort")} />
                            )}
                          </TableCell>
                          <TableCell>{[u.firstName, u.lastName].filter(Boolean).join(" ") || "—"}</TableCell>
                          <TableCell>
                            <Typography variant="caption" sx={monoSx}>
                              {u.externalUserId || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={u.setupError || ""}>
                              <Chip
                                size="small"
                                variant="outlined"
                                color={SETUP_COLORS[u.setupStatus] || "default"}
                                label={t(`superAdmin.integrations.setupStatus.${u.setupStatus}`)}
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell>{formatDate(u.lastLoginAt)}</TableCell>
                          <TableCell align="right">
                            {u.setupStatus === "FAILED" && (
                              <AppIconButton
                                size="small"
                                aria-label={t("superAdmin.integrations.retrySetup")}
                                title={t("superAdmin.integrations.retrySetup")}
                                onClick={() => void onRetrySetup(u)}
                              >
                                <ReplayOutlinedIcon fontSize="small" />
                              </AppIconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <TablePagination
                    component="div"
                    count={users.totalElements}
                    page={usersPage}
                    rowsPerPage={USERS_PAGE_SIZE}
                    rowsPerPageOptions={[USERS_PAGE_SIZE]}
                    onPageChange={(_, page) => setUsersPage(page)}
                  />
                </>
              )
            )}

            {tab === "usage" && (
              detailLoading && !stats ? (
                <Stack alignItems="center" py={3}>
                  <CircularProgress size={24} />
                </Stack>
              ) : stats ? (
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                    {(
                      [
                        ["statsTotal", stats.totalUsers],
                        ["statsEnabled", stats.enabledUsers],
                        ["statsSetupComplete", stats.setupComplete],
                        ["statsSetupPending", stats.setupPending],
                        ["statsSetupFailed", stats.setupFailed],
                        ["statsTickets30", stats.usersWithTicketLast30Days],
                        ["statsLogins30", stats.usersSignedInLast30Days],
                      ] as [string, number][]
                    ).map(([key, value]) => (
                      <Box key={key} sx={{ px: 2, py: 1.25, borderRadius: 1, border: 1, borderColor: "divider", minWidth: 140 }}>
                        <Typography variant="h6">{value}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t(`superAdmin.integrations.${key}`)}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {t("superAdmin.integrations.statsLast", {
                      ticket: formatDate(stats.lastTicketAt),
                      login: formatDate(stats.lastLoginAt),
                    })}
                  </Typography>
                </Stack>
              ) : null
            )}

            {tab === "guide" && (
              <Stack spacing={2}>
                <Typography variant="body2">{t("superAdmin.integrations.guideIntro")}</Typography>
                <Box component="ol" sx={{ m: 0, pl: 2.5, "& li": { mb: 0.5 } }}>
                  <li>
                    <Typography variant="body2">{t("superAdmin.integrations.guideStep1")}</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">{t("superAdmin.integrations.guideStep2")}</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">{t("superAdmin.integrations.guideStep3")}</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">{t("superAdmin.integrations.guideStep4")}</Typography>
                  </li>
                </Box>
                {selectedEmbed && selectedEmbed.authProviders?.ticket !== true && (
                  <Alert severity="warning">{t("superAdmin.integrations.ticketLoginOff", { name: selectedEmbed.name })}</Alert>
                )}
                {selectedEmbed && (
                  <>
                    <Typography variant="subtitle2">{t("superAdmin.integrations.embedSnippet")}</Typography>
                    <Box component="pre" sx={preSx}>
                      {buildEmbedSnippet(selectedEmbed.loaderUrl, selectedEmbed.publicKey)}
                    </Box>
                  </>
                )}
                <Divider />
                <Tabs value={language} onChange={(_, v) => setLanguage(v)}>
                  {SNIPPET_LANGUAGES.map((l) => (
                    <Tab key={l} value={l} label={t(`superAdmin.integrations.lang.${l}`)} />
                  ))}
                </Tabs>
                <Box component="pre" sx={preSx}>
                  {buildIntegrationSnippet(language, apiBase)}
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <AppButton variant="outlined" onClick={() => void copy(buildIntegrationSnippet(language, apiBase))}>
                    {t("superAdmin.integrations.copyCode")}
                  </AppButton>
                  <AppButton
                    variant="outlined"
                    onClick={() => window.open(`${apiBase}/swagger-ui/index.html?urls.primaryName=integration-api`, "_blank", "noopener,noreferrer")}
                  >
                    {t("superAdmin.integrations.openApiDocs")}
                  </AppButton>
                </Stack>
              </Stack>
            )}
          </Box>
        </PageSectionCard>
      )}

      <Dialog open={!!createdSecret} onClose={() => setCreatedSecret(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("superAdmin.integrations.secretCreatedTitle")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Alert severity="warning">{t("superAdmin.integrations.secretCreatedWarning")}</Alert>
            <Box component="pre" sx={preSx}>
              {createdSecret?.secret}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <AppButton variant="outlined" onClick={() => createdSecret && void copy(createdSecret.secret)}>
            {t("superAdmin.integrations.copySecret")}
          </AppButton>
          <AppButton onClick={() => setCreatedSecret(null)}>{t("superAdmin.integrations.secretSaved")}</AppButton>
        </DialogActions>
      </Dialog>

      <AppSlideOver
        open={formOpen}
        onClose={closeForm}
        size="md"
        title={form.id ? t("superAdmin.integrations.editTitle") : t("superAdmin.integrations.createTitle")}
        actions={
          <>
            <AppButton variant="outlined" onClick={closeForm} disabled={saving}>
              {t("common.cancel")}
            </AppButton>
            <AppButton onClick={() => void onSave()} disabled={saving || formInvalid} loading={saving}>
              {form.id ? t("common.save") : t("common.create")}
            </AppButton>
          </>
        }
      >
        {error && formOpen && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <AppFormGrid>
          <AppFormField size={GRID_FULL}>
            <AppTextField
              label={t("superAdmin.integrations.name")}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              autoFocus={!form.id}
              fullWidth
            />
          </AppFormField>
          <AppFormField size={GRID_FULL}>
            <AppAutocomplete<WorkspaceOption>
              label={t("superAdmin.integrations.workspace")}
              options={workspaceOptions}
              value={form.workspaceId ? workspaceOption(form.workspaceId) : null}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              onChange={(_, value) =>
                setForm((f) => ({
                  ...f,
                  workspaceId: value?.id ?? "",
                  // An embed of a different workspace is no longer valid for the new choice.
                  embedConfigId: embedBelongsTo(f.embedConfigId, value?.id ?? "") ? f.embedConfigId : "",
                }))
              }
              helperText={t("superAdmin.integrations.workspaceHelp")}
            />
          </AppFormField>
          <AppFormField size={GRID_HALF}>
            <AppAutocomplete<IntegrationMemberRole>
              label={t("superAdmin.integrations.memberRole")}
              options={MEMBER_ROLES}
              value={form.memberRole}
              disableClearable
              getOptionLabel={(role) => t(`workspace.roles.${role}`)}
              onChange={(_, value) => value && setForm((f) => ({ ...f, memberRole: value }))}
              helperText={t("superAdmin.integrations.memberRoleHelp")}
            />
          </AppFormField>
          <AppFormField size={GRID_HALF}>
            <AppCheckbox
              label={t("superAdmin.integrations.chatbotOnly")}
              checked={form.chatbotOnly}
              onChange={(_, checked) => setForm((f) => ({ ...f, chatbotOnly: checked }))}
            />
            <Typography variant="caption" color="text.secondary" display="block">
              {t("superAdmin.integrations.chatbotOnlyHelp")}
            </Typography>
          </AppFormField>
          <AppFormField size={GRID_FULL}>
            <AppAutocomplete<EmbedOption>
              label={t("superAdmin.integrations.embed")}
              options={embedOptions}
              value={form.embedConfigId ? embedOption(form.embedConfigId) : null}
              getOptionLabel={(o) => o.name}
              getOptionDisabled={(o) => !!o.disabledReason}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderOption={(props, o) => (
                <li {...props} key={o.id}>
                  <Stack>
                    <Typography variant="body2">{o.name}</Typography>
                    {(o.disabledReason || o.note) && (
                      <Typography variant="caption" color={o.disabledReason ? "text.disabled" : "warning.main"}>
                        {o.disabledReason || o.note}
                      </Typography>
                    )}
                  </Stack>
                </li>
              )}
              noOptionsText={t("superAdmin.integrations.noPlatformEmbed")}
              onChange={(_, value) => setForm((f) => ({ ...f, embedConfigId: value?.id ?? "" }))}
              helperText={
                form.embedConfigId && embedOption(form.embedConfigId)?.note
                  ? embedOption(form.embedConfigId)?.note
                  : t("superAdmin.integrations.embedHelp")
              }
            />
          </AppFormField>
          <AppFormField size={GRID_FULL}>
            <AppTextField
              label={t("superAdmin.integrations.allowedIps")}
              value={form.ipsText}
              onChange={(e) => setForm((f) => ({ ...f, ipsText: e.target.value }))}
              multiline
              minRows={2}
              helperText={t("superAdmin.integrations.allowedIpsHelp")}
              fullWidth
            />
          </AppFormField>
          <AppFormField size={GRID_HALF}>
            <AppTextField
              label={t("superAdmin.integrations.rateLimit")}
              type="number"
              value={form.rateLimit}
              onChange={(e) => setForm((f) => ({ ...f, rateLimit: e.target.value }))}
              helperText={t("superAdmin.integrations.rateLimitHelp")}
            />
          </AppFormField>
          <AppFormField size={GRID_HALF}>
            <AppCheckbox
              label={t("superAdmin.integrations.enabled")}
              checked={form.enabled}
              onChange={(_, checked) => setForm((f) => ({ ...f, enabled: checked }))}
            />
          </AppFormField>
          <AppFormField size={GRID_FULL}>
            <AppCheckbox
              label={t("superAdmin.integrations.autoSetupBeas")}
              checked={form.autoSetupBeas}
              onChange={(_, checked) => setForm((f) => ({ ...f, autoSetupBeas: checked }))}
            />
            <Typography variant="caption" color="text.secondary" display="block">
              {t("superAdmin.integrations.autoSetupBeasHelp")}
            </Typography>
          </AppFormField>
          <AppFormField size={GRID_FULL}>
            <AppCheckbox
              label={t("superAdmin.integrations.sendWelcomeMail")}
              checked={form.sendWelcomeMail}
              onChange={(_, checked) => setForm((f) => ({ ...f, sendWelcomeMail: checked }))}
            />
          </AppFormField>
        </AppFormGrid>
      </AppSlideOver>
    </Stack>
  );
}
